import { apiErrorMessage } from '@agos/api-client'
import {
  getGetGiftsProductRulesQueryKey,
  getGetGiftsRulesQueryKey,
  useDeleteGiftsProductRulesRuleId,
  useDeleteGiftsRulesRuleId,
  useGetGiftsProductRules,
  useGetGiftsRules,
  usePatchGiftsProductRulesRuleId,
  usePatchGiftsRulesRuleId,
  usePostGiftsProductRules,
  usePostGiftsRules,
} from '@agos/api-client/menu'
import type { GiftRule, ProductGiftRule } from '@agos/api-client/model'
import { T, formatMoney, tt } from '@agos/shared'
import {
  ConfirmDialog,
  DataTable,
  FilterBar,
  PageHeader,
  StatusBadge,
  cn,
  toast,
  type Column,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Gift, Loader2, Package, Plus, ShoppingBag, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'

/**
 * Ported from legacy `pages/gifts.tsx`. Spec: `docs/pages/manager-menu/gifts.md`.
 *
 * Two independent sections that never talk to each other: gifts triggered by an
 * order's total, and gifts triggered by how many items of a category are in the
 * basket.
 *
 * The toggles are **not** optimistic here, and that is a deliberate departure.
 * Legacy updates local state first and fires the request with
 * `.catch(() => {})` — nothing reads the response and nothing rolls back, so a
 * toggle that failed still reads as applied and a rule believed switched off
 * keeps attaching gifts to real orders (FB-012). The port waits for the server
 * and invalidates, which is a little slower and true.
 */

const PAGE_SIZE = 15

const INPUT_CLASSES =
  'px-4 py-2.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'
const ADD_PANEL_CLASSES = 'bg-card border border-dashed border-border rounded-2xl p-5 space-y-3'
const ACTION_BUTTON_CLASSES =
  'flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity disabled:opacity-50 text-sm'

/** The labelled pill switch both tables use in their Akcie column. */
function PillSwitch({
  label,
  on,
  accent,
  onToggle,
  disabled,
}: {
  label: string
  on: boolean
  accent: 'primary' | 'amber'
  onToggle: () => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-[10px] text-muted-foreground leading-none">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        disabled={disabled}
        onClick={onToggle}
        className={cn(
          'w-10 h-5 rounded-full transition-all relative disabled:opacity-50',
          on ? (accent === 'amber' ? 'bg-amber-500' : 'bg-primary') : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all',
            on ? 'left-5' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}

function DeleteButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={tt('Zmazať')}
      onClick={onClick}
      className="w-8 h-8 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center hover:bg-red-500/20 transition-colors"
    >
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  )
}

interface PendingDeletion {
  kind: 'amount' | 'product'
  id: number
  label: string
}

export function GiftsPage() {
  const queryClient = useQueryClient()

  const { data: amountRules, isLoading: isLoadingAmountRules } = useGetGiftsRules()
  const { data: productRules, isLoading: isLoadingProductRules } = useGetGiftsProductRules()

  const createAmountRule = usePostGiftsRules()
  const updateAmountRule = usePatchGiftsRulesRuleId()
  const deleteAmountRule = useDeleteGiftsRulesRuleId()

  const createProductRule = usePostGiftsProductRules()
  const updateProductRule = usePatchGiftsProductRulesRuleId()
  const deleteProductRule = useDeleteGiftsProductRulesRuleId()

  const [amountSearch, setAmountSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null)

  const [newRuleName, setNewRuleName] = useState('')
  const [newRuleGift, setNewRuleGift] = useState('')
  const [newRuleMinAmount, setNewRuleMinAmount] = useState('')
  const [newRuleShowInKitchen, setNewRuleShowInKitchen] = useState(false)
  const [newRuleError, setNewRuleError] = useState('')

  const [newProductKeyword, setNewProductKeyword] = useState('')
  const [newProductMinCount, setNewProductMinCount] = useState('')
  const [newProductGiftName, setNewProductGiftName] = useState('')
  const [newProductGiftPrice, setNewProductGiftPrice] = useState('')
  const [newProductMultiply, setNewProductMultiply] = useState(false)
  const [newProductShowInKitchen, setNewProductShowInKitchen] = useState(false)
  const [newProductError, setNewProductError] = useState('')

  const refreshAmountRules = () =>
    queryClient.invalidateQueries({ queryKey: getGetGiftsRulesQueryKey() })
  const refreshProductRules = () =>
    queryClient.invalidateQueries({ queryKey: getGetGiftsProductRulesQueryKey() })

  const filteredAmountRules = useMemo(() => {
    const query = amountSearch.trim().toLowerCase()
    if (!query) return amountRules ?? []
    return (amountRules ?? []).filter(
      (rule) =>
        rule.name?.toLowerCase().includes(query) || rule.giftItem?.toLowerCase().includes(query),
    )
  }, [amountRules, amountSearch])

  const filteredProductRules = useMemo(() => {
    const query = productSearch.trim().toLowerCase()
    if (!query) return productRules ?? []
    return (productRules ?? []).filter(
      (rule) =>
        rule.categoryKeyword?.toLowerCase().includes(query) ||
        rule.giftName?.toLowerCase().includes(query),
    )
  }, [productRules, productSearch])

  const addAmountRule = () => {
    // FB-014: legacy returns silently on a blank field. Say what is missing.
    if (!newRuleName.trim() || !newRuleGift.trim() || !newRuleMinAmount.trim()) {
      setNewRuleError(tt('Vyplňte názov, darček aj minimálnu sumu.'))
      return
    }
    const minAmount = Number(newRuleMinAmount)
    if (!Number.isFinite(minAmount) || minAmount < 0) {
      setNewRuleError(tt('Minimálna suma musí byť kladné číslo.'))
      return
    }

    setNewRuleError('')
    createAmountRule.mutate(
      {
        data: {
          name: newRuleName.trim(),
          giftItem: newRuleGift.trim(),
          minAmount,
          showInKitchen: newRuleShowInKitchen,
        },
      },
      {
        onSuccess: () => {
          void refreshAmountRules()
          setNewRuleName('')
          setNewRuleGift('')
          setNewRuleMinAmount('')
          setNewRuleShowInKitchen(false)
          toast({ title: tt('Pridané'), description: tt('Pravidlo darčeka bolo pridané.') })
        },
        // §9.385 - the rule validators refuse by name (a keyword that matches nothing, a gift
        // price above the item's own), and this showed one sentence for all of them.
        onError: (caught) =>
          setNewRuleError(apiErrorMessage(caught, tt('Nepodarilo sa pridať pravidlo.'))),
      },
    )
  }

  const addProductRule = () => {
    const keyword = newProductKeyword.trim().toLowerCase()
    const giftName = newProductGiftName.trim()
    const minCount = Number.parseInt(newProductMinCount, 10)
    const giftPrice = Number(newProductGiftPrice)

    if (!keyword || !giftName) {
      setNewProductError(tt('Vyplňte kľúčové slovo aj názov darčeka.'))
      return
    }
    if (!Number.isInteger(minCount) || minCount < 1) {
      setNewProductError(tt('Minimálny počet musí byť aspoň 1.'))
      return
    }
    if (!Number.isFinite(giftPrice) || giftPrice < 0) {
      setNewProductError(tt('Cena darčeka musí byť kladné číslo.'))
      return
    }

    setNewProductError('')
    createProductRule.mutate(
      {
        data: {
          categoryKeyword: keyword,
          minCount,
          giftName,
          giftPrice,
          multiply: newProductMultiply,
          showInKitchen: newProductShowInKitchen,
        },
      },
      {
        onSuccess: () => {
          void refreshProductRules()
          setNewProductKeyword('')
          setNewProductMinCount('')
          setNewProductGiftName('')
          setNewProductGiftPrice('')
          setNewProductMultiply(false)
          setNewProductShowInKitchen(false)
          toast({ title: tt('Pridané'), description: tt('Pravidlo darčeka bolo pridané.') })
        },
        onError: (caught) =>
          setNewProductError(apiErrorMessage(caught, tt('Nepodarilo sa pridať pravidlo.'))),
      },
    )
  }

  const confirmDeletion = () => {
    if (!pendingDeletion) return
    const { kind, id } = pendingDeletion
    const onSuccess = () => {
      void (kind === 'amount' ? refreshAmountRules() : refreshProductRules())
      setPendingDeletion(null)
      toast({ title: tt('Vymazané'), description: tt('Pravidlo darčeka bolo odstránené.') })
    }

    if (kind === 'amount') deleteAmountRule.mutate({ ruleId: id }, { onSuccess })
    else deleteProductRule.mutate({ ruleId: id }, { onSuccess })
  }

  const amountColumns: Column<GiftRule>[] = [
    {
      key: 'name',
      header: tt('Názov'),
      sortable: true,
      sortValue: (rule) => rule.name ?? '',
      render: (rule) => (
        <span className="font-bold text-foreground">
          <T>{rule.name}</T>
        </span>
      ),
    },
    {
      key: 'giftItem',
      header: tt('Darček'),
      sortable: true,
      sortValue: (rule) => rule.giftItem ?? '',
      render: (rule) => <span className="text-xs font-semibold text-primary">🎁 {rule.giftItem}</span>,
    },
    {
      key: 'minAmount',
      header: tt('Min. suma'),
      sortable: true,
      // Typed `string` by the backend (§14.7); the money utils read either.
      sortValue: (rule) => Number(rule.minAmount ?? 0),
      render: (rule) => (
        <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground flex items-center gap-1 w-fit">
          <ShoppingBag className="w-3 h-3" /> {formatMoney(rule.minAmount)}
        </span>
      ),
    },
    {
      key: 'status',
      header: tt('Stav'),
      sortable: true,
      sortValue: (rule) => (rule.active ? 1 : 0),
      render: (rule) => (
        <StatusBadge
          label={rule.active ? tt('Aktívne') : tt('Neaktívne')}
          color={rule.active ? 'green' : 'zinc'}
        />
      ),
    },
    {
      key: 'kitchen',
      header: tt('Kuchyňa'),
      sortable: true,
      sortValue: (rule) => (rule.showInKitchen ? 1 : 0),
      render: (rule) => (
        <StatusBadge
          label={rule.showInKitchen ? tt('Áno') : tt('Nie')}
          color={rule.showInKitchen ? 'amber' : 'zinc'}
        />
      ),
    },
    {
      key: 'actions',
      header: tt('Akcie'),
      headerClassName: 'text-right',
      className: 'text-right',
      render: (rule) => (
        <div className="flex items-center justify-end gap-2">
          <PillSwitch
            label={tt('Aktívne')}
            accent="primary"
            on={Boolean(rule.active)}
            disabled={updateAmountRule.isPending}
            onToggle={() =>
              rule.id !== undefined &&
              updateAmountRule.mutate(
                { ruleId: rule.id, data: { active: !rule.active } },
                { onSuccess: () => void refreshAmountRules() },
              )
            }
          />
          <PillSwitch
            label={tt('Kuchyňa')}
            accent="amber"
            on={Boolean(rule.showInKitchen)}
            disabled={updateAmountRule.isPending}
            onToggle={() =>
              rule.id !== undefined &&
              updateAmountRule.mutate(
                { ruleId: rule.id, data: { showInKitchen: !rule.showInKitchen } },
                { onSuccess: () => void refreshAmountRules() },
              )
            }
          />
          <DeleteButton
            onClick={() =>
              rule.id !== undefined &&
              setPendingDeletion({ kind: 'amount', id: rule.id, label: rule.name ?? '' })
            }
          />
        </div>
      ),
    },
  ]

  const productColumns: Column<ProductGiftRule>[] = [
    {
      key: 'categoryKeyword',
      header: tt('Kategória'),
      sortable: true,
      sortValue: (rule) => rule.categoryKeyword ?? '',
      render: (rule) => (
        <span className="font-bold text-foreground capitalize">{rule.categoryKeyword}</span>
      ),
    },
    {
      key: 'giftName',
      header: tt('Darček'),
      sortable: true,
      sortValue: (rule) => rule.giftName ?? '',
      render: (rule) => (
        <span className="text-xs font-semibold text-primary">
          🎁 {rule.giftName} – {formatMoney(rule.giftPrice)}
        </span>
      ),
    },
    {
      key: 'minCount',
      header: tt('Min. počet'),
      sortable: true,
      sortValue: (rule) => rule.minCount ?? 0,
      render: (rule) => (
        <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground w-fit inline-block">
          {rule.minCount} ks
        </span>
      ),
    },
    {
      key: 'multiply',
      header: tt('Násobiť'),
      sortable: true,
      sortValue: (rule) => (rule.multiply ? 1 : 0),
      render: (rule) =>
        rule.multiply ? (
          <StatusBadge label={tt('Násobiť')} color="blue" />
        ) : (
          <span className="text-muted-foreground text-xs">-</span>
        ),
    },
    {
      key: 'status',
      header: tt('Stav'),
      sortable: true,
      sortValue: (rule) => (rule.active ? 1 : 0),
      render: (rule) => (
        <StatusBadge
          label={rule.active ? tt('Aktívne') : tt('Neaktívne')}
          color={rule.active ? 'green' : 'zinc'}
        />
      ),
    },
    {
      key: 'kitchen',
      header: tt('Kuchyňa'),
      sortable: true,
      sortValue: (rule) => (rule.showInKitchen ? 1 : 0),
      render: (rule) => (
        <StatusBadge
          label={rule.showInKitchen ? tt('Áno') : tt('Nie')}
          color={rule.showInKitchen ? 'amber' : 'zinc'}
        />
      ),
    },
    {
      key: 'actions',
      header: tt('Akcie'),
      headerClassName: 'text-right',
      className: 'text-right',
      render: (rule) => (
        <div className="flex items-center justify-end gap-2">
          <PillSwitch
            label={tt('Aktívne')}
            accent="primary"
            on={Boolean(rule.active)}
            disabled={updateProductRule.isPending}
            onToggle={() =>
              rule.id !== undefined &&
              updateProductRule.mutate(
                { ruleId: rule.id, data: { active: !rule.active } },
                { onSuccess: () => void refreshProductRules() },
              )
            }
          />
          <PillSwitch
            label={tt('Kuchyňa')}
            accent="amber"
            on={Boolean(rule.showInKitchen)}
            disabled={updateProductRule.isPending}
            onToggle={() =>
              rule.id !== undefined &&
              updateProductRule.mutate(
                { ruleId: rule.id, data: { showInKitchen: !rule.showInKitchen } },
                { onSuccess: () => void refreshProductRules() },
              )
            }
          />
          <DeleteButton
            onClick={() =>
              rule.id !== undefined &&
              setPendingDeletion({ kind: 'product', id: rule.id, label: rule.giftName ?? '' })
            }
          />
        </div>
      ),
    },
  ]

  const productThreshold = Number.parseInt(newProductMinCount || '1', 10)

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <PageHeader
          title={tt('Darčeky podľa sumy')}
          description={tt(
            'Nastavte automatické darčeky pridávané k objednávke podľa minimálnej sumy.',
          )}
          icon={<Gift className="w-5 h-5" />}
        />

        <FilterBar
          search={{
            value: amountSearch,
            onChange: setAmountSearch,
            placeholder: tt('Hľadať pravidlo...'),
          }}
        />

        {isLoadingAmountRules ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> <T>Načítavam...</T>
          </div>
        ) : (
          <DataTable<GiftRule>
            columns={amountColumns}
            data={filteredAmountRules}
            rowKey={(rule) => rule.id ?? 0}
            pageSize={PAGE_SIZE}
            emptyMessage={tt('Zatiaľ žiadne pravidlá darčekov.')}
          />
        )}

        <div className={ADD_PANEL_CLASSES}>
          <p className="text-sm font-bold text-foreground flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" /> <T>Nové pravidlo darčeka</T>
          </p>
          <div className="grid grid-cols-2 gap-3">
            <input
              value={newRuleName}
              onChange={(event) => setNewRuleName(event.target.value)}
              placeholder={tt('Názov pravidla')}
              className={cn(INPUT_CLASSES, 'col-span-2')}
            />
            <input
              value={newRuleGift}
              onChange={(event) => setNewRuleGift(event.target.value)}
              placeholder={tt('Darček (napr. Kola zadarmo)')}
              className={INPUT_CLASSES}
            />
            <input
              value={newRuleMinAmount}
              onChange={(event) => setNewRuleMinAmount(event.target.value)}
              type="number"
              placeholder={tt('Min. suma objednávky (€)')}
              className={INPUT_CLASSES}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <PillSwitch
              label=""
              accent="amber"
              on={newRuleShowInKitchen}
              onToggle={() => setNewRuleShowInKitchen((enabled) => !enabled)}
            />
            <span className="text-sm text-foreground">
              <T>Zobraz darček v kuchyni</T>
            </span>
          </label>
          {newRuleError && <p className="text-xs text-destructive">{newRuleError}</p>}
          <button
            type="button"
            onClick={addAmountRule}
            disabled={createAmountRule.isPending}
            className={ACTION_BUTTON_CLASSES}
          >
            {createAmountRule.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> <T>Pridávam...</T>
              </>
            ) : (
              tt('Pridať pravidlo')
            )}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <PageHeader
          title={tt('Pravidlá podľa počtu produktov')}
          description={tt(
            'Darček sa ponúkne zákazníkovi keď má v košíku určitý počet produktov z danej kategórie.',
          )}
          icon={<Package className="w-5 h-5" />}
        />

        <FilterBar
          search={{
            value: productSearch,
            onChange: setProductSearch,
            placeholder: tt('Hľadať pravidlo...'),
          }}
        />

        {isLoadingProductRules ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> <T>Načítavam...</T>
          </div>
        ) : (
          <DataTable<ProductGiftRule>
            columns={productColumns}
            data={filteredProductRules}
            rowKey={(rule) => rule.id ?? 0}
            pageSize={PAGE_SIZE}
            emptyMessage={tt('Zatiaľ žiadne pravidlá.')}
          />
        )}

        <div className={ADD_PANEL_CLASSES}>
          <p className="text-sm font-bold text-foreground flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" /> <T>Nové pravidlo podľa počtu produktov</T>
          </p>
          <div className="grid grid-cols-2 gap-3">
            <input
              value={newProductKeyword}
              onChange={(event) => setNewProductKeyword(event.target.value)}
              placeholder={tt('Kľúčové slovo kategórie (napr. pizza)')}
              className={cn(INPUT_CLASSES, 'col-span-2')}
            />
            <input
              value={newProductMinCount}
              onChange={(event) => setNewProductMinCount(event.target.value)}
              type="number"
              min="1"
              placeholder={tt('Min. počet produktov')}
              className={INPUT_CLASSES}
            />
            <input
              value={newProductGiftPrice}
              onChange={(event) => setNewProductGiftPrice(event.target.value)}
              type="number"
              min="0"
              step="0.01"
              placeholder={tt('Cena darčeka (€)')}
              className={INPUT_CLASSES}
            />
            <input
              value={newProductGiftName}
              onChange={(event) => setNewProductGiftName(event.target.value)}
              placeholder={tt('Názov darčeka (napr. Pizza Šunková)')}
              className={cn(INPUT_CLASSES, 'col-span-2')}
            />
          </div>
          {newProductMinCount && (
            <p className="text-xs text-muted-foreground">
              <T>Darček bude pridaný ak bude počet</T> {newProductKeyword || tt('produktov')}{' '}
              <T>v objednávke väčší ako</T>{' '}
              {Math.max(0, (Number.isFinite(productThreshold) ? productThreshold : 1) - 1)}
            </p>
          )}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <PillSwitch
              label=""
              accent="primary"
              on={newProductMultiply}
              onToggle={() => setNewProductMultiply((enabled) => !enabled)}
            />
            <span className="text-sm text-foreground">
              <T>Násobiť darček (napr. pri 4 pizziach s prahom 2 = 2× darček)</T>
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <PillSwitch
              label=""
              accent="amber"
              on={newProductShowInKitchen}
              onToggle={() => setNewProductShowInKitchen((enabled) => !enabled)}
            />
            <span className="text-sm text-foreground">
              <T>Zobraz darček v kuchyni</T>
            </span>
          </label>
          {newProductError && <p className="text-xs text-destructive">{newProductError}</p>}
          <button
            type="button"
            onClick={addProductRule}
            disabled={createProductRule.isPending}
            className={ACTION_BUTTON_CLASSES}
          >
            {createProductRule.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> <T>Pridávam...</T>
              </>
            ) : (
              tt('Pridať pravidlo')
            )}
          </button>
        </div>
      </div>

      {/* FB-013: legacy deletes on a single click, with no confirmation and no
          check that the server agreed. */}
      <ConfirmDialog
        open={pendingDeletion !== null}
        onClose={() => setPendingDeletion(null)}
        onConfirm={confirmDeletion}
        loading={deleteAmountRule.isPending || deleteProductRule.isPending}
        variant="danger"
        title={tt('Vymazať pravidlo darčeka?')}
        description={`${tt('Pravidlo')} "${pendingDeletion?.label ?? ''}" ${tt('sa odstráni a darček sa prestane pridávať k objednávkam.')}`}
        confirmLabel={tt('Vymazať')}
      />
    </div>
  )
}
